/**
 * Parse CSV exportado de Meta Lead Ads (Ads Manager).
 * Soporta separador coma (,) o punto y coma (;).
 * Charset: UTF-8, Windows-1252 y reparación de mojibake clásico.
 * @module-isolated analytics-meta-csv-parse
 */

function stripBom(text) {
  return String(text || '').replace(/^\uFEFF/, '');
}

/**
 * Puntúa calidad de texto español (más alto = mejor).
 * @param {string} text
 * @returns {number}
 */
export function scoreDecodedText(text) {
  const s = String(text || '');
  if (!s) return -1e9;
  let score = 0;
  const replacement = (s.match(/\uFFFD/g) || []).length;
  score -= replacement * 50;
  // Mojibake típico UTF-8 leído como Latin-1: DarÃ­o, MalbrÃ¡n
  const moji = (s.match(/Ã.|Â.|â€/g) || []).length;
  score -= moji * 25;
  const good = (s.match(/[áéíóúñüÁÉÍÓÚÑÜ¿¡]/g) || []).length;
  score += good * 8;
  score += Math.min(200, (s.match(/[A-Za-zÁÉÍÓÚáéíóúñÑ]/g) || []).length);
  return score;
}

/**
 * UTF-8 leído como Latin-1/Windows-1252 → reinterpreta bytes 0–255 como UTF-8.
 * Ej: "DarÃ­o" → "Darío". Si no mejora, devuelve el original.
 * @param {string} text
 * @returns {string}
 */
export function repairClassicMojibake(text) {
  const s = String(text || '');
  if (!s || !/[ÃÂâ]/.test(s)) return s;
  try {
    const bytes = new Uint8Array(s.length);
    for (let i = 0; i < s.length; i++) {
      const code = s.charCodeAt(i);
      if (code > 255) return s;
      bytes[i] = code;
    }
    const fixed = new TextDecoder('utf-8', { fatal: false }).decode(bytes);
    if (scoreDecodedText(fixed) > scoreDecodedText(s) + 5) return fixed;
  } catch (e) {
    /* keep original */
  }
  return s;
}

/**
 * Lee bytes de un CSV/TXT Meta con el charset más plausible.
 * @param {ArrayBuffer} buffer
 * @returns {{ text: string, encoding: string }}
 */
export function decodeCsvBytes(buffer) {
  const bytes = new Uint8Array(buffer || new ArrayBuffer(0));
  let start = 0;
  if (bytes.length >= 3 && bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf) {
    start = 3;
  }
  const slice = bytes.subarray(start);
  const candidates = [];

  try {
    const utf8 = new TextDecoder('utf-8').decode(slice);
    candidates.push({ text: utf8, encoding: 'utf-8' });
    candidates.push({
      text: repairClassicMojibake(utf8),
      encoding: 'utf-8+mojibake-repair'
    });
  } catch (e) {
    /* ignore */
  }

  try {
    const win = new TextDecoder('windows-1252').decode(slice);
    candidates.push({ text: win, encoding: 'windows-1252' });
    candidates.push({
      text: repairClassicMojibake(win),
      encoding: 'windows-1252+mojibake-repair'
    });
  } catch (e) {
    /* ignore */
  }

  try {
    const latin1 = new TextDecoder('iso-8859-1').decode(slice);
    candidates.push({ text: latin1, encoding: 'iso-8859-1' });
    candidates.push({
      text: repairClassicMojibake(latin1),
      encoding: 'iso-8859-1+mojibake-repair'
    });
  } catch (e) {
    /* ignore */
  }

  if (!candidates.length) {
    return { text: '', encoding: 'empty' };
  }

  let best = candidates[0];
  let bestScore = scoreDecodedText(best.text);
  for (let i = 1; i < candidates.length; i++) {
    const sc = scoreDecodedText(candidates[i].text);
    if (sc > bestScore) {
      best = candidates[i];
      bestScore = sc;
    }
  }
  return { text: stripBom(best.text), encoding: best.encoding };
}

/**
 * @param {File|Blob} file
 * @returns {Promise<{ text: string, encoding: string }>}
 */
export async function readCsvFileText(file) {
  const buffer = await file.arrayBuffer();
  return decodeCsvBytes(buffer);
}

/**
 * Detecta separador: el que más aparece en la 1ª línea fuera de comillas.
 * @param {string} text
 * @returns {','|';'}
 */
function detectDelimiter(text) {
  const firstLine = String(text || '').split(/\r?\n/)[0] || '';
  let commas = 0;
  let semis = 0;
  let inQuotes = false;
  for (let i = 0; i < firstLine.length; i++) {
    const ch = firstLine[i];
    if (ch === '"') {
      if (inQuotes && firstLine[i + 1] === '"') {
        i++;
        continue;
      }
      inQuotes = !inQuotes;
      continue;
    }
    if (inQuotes) continue;
    if (ch === ',') commas++;
    if (ch === ';') semis++;
  }
  return semis > commas ? ';' : ',';
}

/**
 * Parser CSV/TSV-like (comillas, coma o punto y coma, CRLF).
 * @param {string} text
 * @param {string=} delimiter
 * @returns {string[][]}
 */
export function parseCsvText(text, delimiter) {
  const src = stripBom(text);
  const sep = delimiter || detectDelimiter(src);
  const rows = [];
  let row = [];
  let cell = '';
  let inQuotes = false;
  for (let i = 0; i < src.length; i++) {
    const ch = src[i];
    const next = src[i + 1];
    if (inQuotes) {
      if (ch === '"' && next === '"') {
        cell += '"';
        i++;
      } else if (ch === '"') {
        inQuotes = false;
      } else {
        cell += ch;
      }
      continue;
    }
    if (ch === '"') {
      inQuotes = true;
      continue;
    }
    if (ch === sep) {
      row.push(cell);
      cell = '';
      continue;
    }
    if (ch === '\n') {
      row.push(cell);
      rows.push(row);
      row = [];
      cell = '';
      continue;
    }
    if (ch === '\r') continue;
    cell += ch;
  }
  if (cell.length || row.length) {
    row.push(cell);
    rows.push(row);
  }
  return rows.filter((r) => r.some((c) => String(c || '').trim() !== ''));
}

function normHeader(h) {
  return String(h || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[\s_-]+/g, ' ');
}

function pickCol(map, aliases) {
  for (let i = 0; i < aliases.length; i++) {
    const key = normHeader(aliases[i]);
    if (map[key] != null) return map[key];
  }
  return -1;
}

/**
 * Meta a veces exporta tipado: l:123, p:+569…, c:120…, ag:…, as:…, f:…
 * @param {string} raw
 * @returns {string}
 */
export function stripMetaTypedValue(raw) {
  let s = String(raw == null ? '' : raw).trim();
  if (!s) return '';
  s = s.replace(/^[a-z]{1,3}:/i, '').trim();
  return s;
}

/**
 * @param {string} csvText
 * @param {{ encoding?: string }=} meta
 * @returns {{ ok: boolean, error?: string, leads: Array<Object>, headers: string[], encoding?: string, totalRows?: number }}
 */
export function parseMetaLeadAdsCsv(csvText, meta) {
  const matrix = parseCsvText(csvText);
  if (!matrix.length) {
    return { ok: false, error: 'CSV vacío.', leads: [], headers: [] };
  }
  const headers = matrix[0].map((h) => String(h || '').trim());
  const map = {};
  headers.forEach((h, idx) => {
    map[normHeader(h)] = idx;
  });

  const colId = pickCol(map, [
    'id',
    'lead id',
    'lead_id',
    'meta lead id',
    'id del lead',
    'identificador'
  ]);
  const colName = pickCol(map, [
    'full_name',
    'full name',
    'nombre completo',
    'nombre',
    'name',
    'full name'
  ]);
  const colFirst = pickCol(map, ['first_name', 'first name', 'nombre']);
  const colLast = pickCol(map, ['last_name', 'last name', 'apellido']);
  const colEmail = pickCol(map, ['email', 'e-mail', 'correo', 'correo electronico']);
  const colPhone = pickCol(map, [
    'phone_number',
    'phone number',
    'phone',
    'telefono',
    'teléfono',
    'whatsapp',
    'número de teléfono',
    'numero de telefono'
  ]);
  const colCampName = pickCol(map, [
    'campaign_name',
    'campaign name',
    'nombre de la campaña',
    'nombre de la campana',
    'campaña',
    'campana'
  ]);
  const colCampId = pickCol(map, ['campaign_id', 'campaign id', 'id de la campaña', 'id de la campana']);
  const colAdName = pickCol(map, ['ad_name', 'ad name', 'nombre del anuncio', 'anuncio']);
  const colForm = pickCol(map, ['form_id', 'form id', 'formulario', 'id del formulario']);
  const colCreated = pickCol(map, [
    'created_time',
    'created time',
    'created_at',
    'fecha de creación',
    'fecha de creacion',
    'fecha'
  ]);

  if (colPhone < 0 && colEmail < 0) {
    return {
      ok: false,
      error:
        'No encontré columnas de teléfono ni email. ¿Es el CSV de leads de Lead Ads (no el de insights)?',
      leads: [],
      headers
    };
  }

  const leads = [];
  for (let r = 1; r < matrix.length; r++) {
    const row = matrix[r];
    const get = (idx) => {
      if (idx < 0 || idx >= row.length) return '';
      return repairClassicMojibake(String(row[idx] || '').trim());
    };
    let nombre = get(colName);
    if (!nombre) {
      const fn = get(colFirst);
      const ln = get(colLast);
      nombre = (fn + ' ' + ln).trim();
    }
    const metaLeadId = stripMetaTypedValue(get(colId));
    const email = get(colEmail);
    const telefono = stripMetaTypedValue(get(colPhone));
    if (!metaLeadId && !email && !telefono && !nombre) continue;
    leads.push({
      rowKey: metaLeadId || 'csv_' + r,
      metaLeadId,
      nombre,
      email,
      telefono,
      campaignName: get(colCampName),
      campaignId: stripMetaTypedValue(get(colCampId)),
      adName: get(colAdName),
      formId: stripMetaTypedValue(get(colForm)),
      createdAt: get(colCreated)
    });
  }

  return {
    ok: true,
    leads,
    headers,
    totalRows: leads.length,
    encoding: meta && meta.encoding ? meta.encoding : undefined
  };
}
